import { useEffect, useState } from 'react'
import {
  IconButton,
  TableCell,
  TableRow,
  useTheme,
  Typography,
  Box,
  Tooltip,
  useMediaQuery,
  Button,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'

import { useTranslation } from 'react-i18next'

import {
  ERewardActivityStatus,
  IConsumerScanDeclineReason,
  IConsumerScanRespDto,
  IScanToEarnSettingsModal,
} from 'src/types'
import { truncateString } from 'src/common/utils'
import {
  useItemsRemoveConfirm,
  useMe,
  useRemoveConsumerScans,
  useUpdateConsumerScanStatus,
} from 'src/hooks'

import { useStyles } from './../styles'
import { ChevronDownIcon } from 'src/assets/icons/v2/ChevronDownIcon'
import { ChevronUpIcon } from 'src/assets/icons/v2/ChevronUpIcon'

import { ItemWithPopover } from 'src/components/item-with-popover'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { AppEvents, FONTS } from 'src/constants'

import { TextTag } from 'src/components/text-tag'
import { getColorOrNameByConsumerScanStatus } from 'src/common/utils/get-color-or name-by-scan-status'

import { BodyRowCollapse } from './collapsed'
import { DeclineReasonModal } from './decline-reason-modal.component'
import { TrashIcon } from 'src/assets/icons/v2/TrashIcon'
import { GearIcon } from 'src/assets/icons/v2/GearIcon'
import { UtcAndLocalDatesCell } from 'src/components/utc-and-local-dates-cell'

import { ConfirmApproveModal } from './confirm-approve-modal.component'
import { useGetApprovalFeedbacksOptions } from 'src/hooks/approval-feedbacks'

interface IProps {
  item: IConsumerScanRespDto
  isOpen: boolean
  handleDeclineInfoModalOpen: (declineReason: string) => void
  handleSettingsModalOpen: (data: IScanToEarnSettingsModal) => void
}

export const TableBodyRow = ({
  item,
  handleDeclineInfoModalOpen,
  isOpen = false,
  handleSettingsModalOpen,
}: IProps) => {
  const [itemStatus, setItemStatus] = useState<ERewardActivityStatus>(item.rewardActivity.status)

  const [t, i18n] = useTranslation()
  const [toggle, setToggle] = useState(isOpen)
  const [isDeclineReasonModalOpen, setIsDeclineReasonModalOpen] = useState(isOpen)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)

  const { data: curUser } = useMe()
  const { mutate: updateStatus, isLoading: isUpdatingStatusLoading } = useUpdateConsumerScanStatus()
  const { mutateAsync: removeConsumerScans, isLoading: isRemovingLoading } =
    useRemoveConsumerScans()
  const { data: approvalOptions, isLoading: isApprovalOptionsLoading } =
    useGetApprovalFeedbacksOptions()

  const theme = useTheme()
  const classes = useStyles()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'))

  const { setRemoveConfirm } = useItemsRemoveConfirm()

  useEffect(() => {
    setToggle(isOpen)
  }, [isOpen])

  useEffect(() => {
    setItemStatus(item.rewardActivity.status)
  }, [item.rewardActivity.status])

  const handleCollapse = () => {
    setToggle((toggle) => !toggle)
  }

  const handleItemsRemove = async (item: IConsumerScanRespDto) => {
    if (!item) return

    setRemoveConfirm({
      handleConfirm: async () => {
        await removeConsumerScans([item.id])
      },
      itemsTitles: [item.barcode_id],
      itemsDetails: [
        { title: 'Product Name', text: item.product_name },
        { title: 'Account Name', text: item.company_name },
        { title: 'Status', text: item.rewardActivity.status },
      ],

      itemsRelations: [],

      itemsType: 'consumer scan',
    })
  }

  const handleDeclineReasonModalClose = () => {
    setIsDeclineReasonModalOpen(false)
  }

  const handleApproveModalClose = () => {
    setIsApproveModalOpen(false)
  }

  const handleConsumerScanStatus = (status: ERewardActivityStatus) => {
    if (status === ERewardActivityStatus.DECLINED) {
      setIsDeclineReasonModalOpen(true)

      return
    }

    if (status === ERewardActivityStatus.APPROVED) {
      setIsApproveModalOpen(true)

      return
    }
  }

  const handleDeclineConsumerScan = (reason: IConsumerScanDeclineReason) => {
    setItemStatus(ERewardActivityStatus.DECLINED)
    updateStatus({ id: item.id, status: ERewardActivityStatus.DECLINED, decline_reason: reason })
  }

  const handleApproveConsumerScan = (items: string[]) => {
    setItemStatus(ERewardActivityStatus.APPROVED)
    updateStatus({ id: item.id, status: ERewardActivityStatus.APPROVED, approval_feedbacks: items })
  }

  const handleCopyClick = (link?: string) => {
    if (!link) {
      return
    }

    const detail = { message: t('clipboard-copied-notification.v') }
    const event = new CustomEvent(AppEvents.HTTP_SUCCESS, { detail })
    document.dispatchEvent(event)

    navigator.clipboard.writeText(link)
  }

  // const isHighlighted = item.accountExists === false || item.productExists === false
  const isHighlighted = false

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: isHighlighted
            ? theme.palette.custom.lightGreen
            : theme.palette.custom.white,
        }}
      >
        <TableCell sx={{ borderBottom: 'unset', padding: '0px 0px 0px 4px' }}>
          <IconButton onClick={handleCollapse}>
            {toggle ? (
              <ChevronUpIcon size={28} color={theme.palette.custom.darkBlue2} />
            ) : (
              <ChevronDownIcon size={28} color={theme.palette.custom.darkBlue2} />
            )}
          </IconButton>
        </TableCell>
        <TableCell align="center" sx={{ borderBottom: 'unset', padding: 0 }}>
          {isMobile && (
            <p style={{ fontSize: '10px', margin: '4px 0px 4px 0px' }}>
              {truncateString(item.user?.name || item.user.email, 12)}
            </p>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            {itemStatus === ERewardActivityStatus.DECLINED ? (
              <Button
                disabled={!item?.rewardActivity?.payload?.decline_reason}
                onClick={() => {
                  handleDeclineInfoModalOpen(item?.rewardActivity?.payload?.decline_reason || '')
                }}
                sx={{ textTransform: 'none', p: '4px' }}
              >
                <TextTag
                  label={getColorOrNameByConsumerScanStatus('text', itemStatus)}
                  bgColor={getColorOrNameByConsumerScanStatus('color', itemStatus)}
                />
              </Button>
            ) : (
              <TextTag
                label={getColorOrNameByConsumerScanStatus('text', itemStatus)}
                bgColor={getColorOrNameByConsumerScanStatus('color', itemStatus)}
              />
            )}
          </Box>
          {isMobile && (
            <UtcAndLocalDatesCell
              date={item.created_at}
              loalTimezone={curUser?.timezone}
              localDateFontSize="10px"
              utcDateFontSize="9px"
            />
          )}
        </TableCell>
        {!isMobile && (
          <TableCell sx={{ borderBottom: 'unset', padding: 0 }}>
            <ItemWithPopover
              item={
                <Typography
                  sx={{
                    py: '6px',
                    fontSize: 11,
                    color: theme.palette.custom.darkBlue2,
                    fontWeight: FONTS.WEIGHTS.bold,
                    minWidth: '60px',
                  }}
                >
                  {item.user?.name || '-'}
                </Typography>
              }
              subtitle={
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '5px',
                  }}
                >
                  {item.user.email}
                  <IconButton
                    sx={{
                      padding: '0.375rem',
                    }}
                    onClick={() => {
                      handleCopyClick(item.user.email)
                    }}
                  >
                    <ContentCopyIcon className={classes.linkIcon} />
                  </IconButton>
                </Box>
              }
              // cursor="help"
            />
          </TableCell>
        )}

        <TableCell
          sx={{
            paddingX: '8px',
            borderBottom: 'unset',
            padding: '4px 0px 4px 4px',
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: '3px',
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: isMobile ? '600' : '',
              }}
            >
              {isTablet ? truncateString(item.company_name) : item.company_name}
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? '10px' : '14px',
              }}
            >
              {isTablet ? truncateString(item.product_name) : item.product_name}
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? '10px' : '12px',
                color: theme.palette.custom.grey2,
              }}
            >
              {item.barcode_id}
            </Typography>

            {/* <Typography
              sx={{
                fontSize: '12px',
              }}
            >
              {capitalizeFirstLetter(item.product_type)}
            </Typography> */}
          </Box>
        </TableCell>

        {!isMobile && (
          <TableCell sx={{ paddingX: '8px', borderBottom: 'unset' }} align="center">
            {item.rewardActivity.amount}
          </TableCell>
        )}

        {!isMobile && (
          <TableCell align="center" sx={{ fontSize: '10px', borderBottom: 'unset' }}>
            <UtcAndLocalDatesCell date={item.created_at} loalTimezone={curUser?.timezone} />
          </TableCell>
        )}

        <TableCell align="center" sx={{ fontSize: '10px', borderBottom: 'unset', padding: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {itemStatus === ERewardActivityStatus.PENDING && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
              >
                <Tooltip title="Approve Scan">
                  <IconButton
                    sx={{
                      padding: '4px',
                    }}
                    disabled={isUpdatingStatusLoading}
                    onClick={handleConsumerScanStatus.bind(null, ERewardActivityStatus.APPROVED)}
                  >
                    <CheckIcon sx={{ color: 'green' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Decline Scan">
                  <IconButton
                    sx={{
                      padding: '4px',
                    }}
                    disabled={isUpdatingStatusLoading}
                    onClick={handleConsumerScanStatus.bind(this, ERewardActivityStatus.DECLINED)}
                  >
                    <ClearIcon sx={{ color: '#E57373' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box>
              {item.productExists === false && (
                <IconButton
                  sx={{
                    padding: '3px',
                  }}
                  onClick={() => {
                    handleSettingsModalOpen({
                      accountExists: item.accountExists,
                      productExists: item.productExists,
                      accountName: item.company_name,
                      productName: item.product_name,
                      consumerScanId: item.id,
                    })
                  }}
                >
                  <GearIcon size={26} />
                </IconButton>
              )}
              {itemStatus !== ERewardActivityStatus.APPROVED && (
                <IconButton
                  disabled={isRemovingLoading}
                  sx={{
                    padding: '3px',
                  }}
                  onClick={() => {
                    handleItemsRemove(item)
                  }}
                >
                  <TrashIcon size={26} />
                </IconButton>
              )}
            </Box>
          </Box>
        </TableCell>
      </TableRow>

      <BodyRowCollapse
        item={item}
        collapsed={toggle}
        isHighlighted={false}
        userTz={curUser?.timezone}
      />

      <ConfirmApproveModal
        options={approvalOptions || []}
        handleClose={handleApproveModalClose}
        isLoadingModal={isApprovalOptionsLoading || isUpdatingStatusLoading}
        open={isApproveModalOpen}
        handleConfirm={handleApproveConsumerScan}
        itemsDetails={[
          { text: item.barcode_id, title: 'Barcode ID' },
          { text: item.product_name, title: 'Product Name' },
          { text: item.company_name, title: 'Account Name' },
        ]}
      />

      <DeclineReasonModal
        handleClose={handleDeclineReasonModalClose}
        handleConfirmDecline={handleDeclineConsumerScan}
        confirmModalHandler={setRemoveConfirm}
        open={isDeclineReasonModalOpen}
        item={item}
      />
    </>
  )
}
